'use client';

import React, { useState } from 'react';
import { FileCode2, Loader2, Upload, X } from 'lucide-react';
import type { PageSection } from '@/lib/storefront/section-library';
import { createSectionId, DEFAULT_SECTION_STYLE } from '@/lib/storefront/section-templates';

type Source = 'paste' | 'html' | 'css' | 'zip';

interface ImportCustomSectionModalProps {
  open: boolean;
  importUrl: string;
  onClose: () => void;
  onImported: (section: PageSection, warnings: string[]) => void;
}

function importErrorMessage(data: unknown, status: number): string {
  if (data !== null && typeof data === 'object') {
    const record = data as { error?: unknown; message?: unknown; errors?: unknown };
    if (typeof record.error === 'string' && record.error.trim()) return record.error;
    if (typeof record.message === 'string' && record.message.trim()) return record.message;
    if (record.errors && typeof record.errors === 'object') {
      const parts = Object.values(record.errors as Record<string, unknown>)
        .flat()
        .filter((item): item is string => typeof item === 'string');
      if (parts.length > 0) return parts.join(' ');
    }
  }
  return `Import failed (${status}).`;
}

export function ImportCustomSectionModal({
  open,
  importUrl,
  onClose,
  onImported,
}: ImportCustomSectionModalProps) {
  const [source, setSource] = useState<Source>('paste');
  const [name, setName] = useState('Custom section');
  const [html, setHtml] = useState('');
  const [css, setCss] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);

  if (!open) return null;

  async function submit() {
    if (!importUrl) {
      setError('Import needs a platform URL.');
      return;
    }
    setPending(true);
    setError(null);
    setWarnings([]);
    try {
      const body = new FormData();
      body.append('source', source);
      body.append('name', name.trim() || 'Custom section');

      if (source === 'paste') {
        if (!html.trim()) {
          setError('Paste some HTML to import.');
          setPending(false);
          return;
        }
        body.append('html', html);
        if (css.trim()) body.append('css', css);
      } else if (source === 'css') {
        if (html.trim()) body.append('html', html);
        if (css.trim()) body.append('css', css);
        if (file) body.append('file', file);
        if (!file && !css.trim()) {
          setError('Upload a .css file or paste CSS.');
          setPending(false);
          return;
        }
      } else if (file) {
        body.append('file', file);
        if (source === 'html' && css.trim()) body.append('css', css);
      } else {
        setError(`Choose a ${source === 'zip' ? 'ZIP' : `.${source}`} file to upload.`);
        setPending(false);
        return;
      }

      const res = await fetch(importUrl, {
        method: 'POST',
        body,
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(importErrorMessage(data, res.status));
        return;
      }

      const raw = data.section;
      if (!raw || typeof raw !== 'object') {
        setError('Import did not return a section.');
        return;
      }

      const section: PageSection = {
        id: typeof raw.id === 'string' ? raw.id : createSectionId(),
        category: 'custom',
        variationId: typeof raw.variationId === 'string' ? raw.variationId : 'html',
        content: typeof raw.content === 'object' && raw.content ? raw.content : {},
        style: { ...DEFAULT_SECTION_STYLE, ...(raw.style ?? {}) },
      };
      const nextWarnings = Array.isArray(data.warnings)
        ? data.warnings.filter((item: unknown): item is string => typeof item === 'string')
        : [];
      setWarnings(nextWarnings);
      onImported(section, nextWarnings);
      onClose();
    } catch {
      setError('Import failed. Check your connection and try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="import-custom-title">
      <div className="flex max-h-[90vh] w-full max-w-xl flex-col overflow-hidden rounded-2xl border border-line bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div>
            <h2 id="import-custom-title" className="text-sm font-semibold text-ink">Import custom section</h2>
            <p className="text-[11px] text-muted">HTML, CSS, or a ZIP package. Scripts are stripped.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-muted hover:bg-shell" aria-label="Close import">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-4 gap-1">
            {([
              ['paste', 'Paste HTML'],
              ['html', 'HTML file'],
              ['css', 'CSS + HTML'],
              ['zip', 'ZIP'],
            ] as const).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setSource(id);
                  setFile(null);
                  setError(null);
                }}
                className={`rounded-lg border px-2 py-2 text-[10px] font-semibold ${
                  source === id ? 'border-brand bg-brand/10 text-brand' : 'border-line text-muted'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="block text-xs font-medium text-ink">
            Section name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 text-xs text-ink outline-none focus:border-brand"
            />
          </label>

          {(source === 'paste' || source === 'css') && (
            <label className="block text-xs font-medium text-ink">
              {source === 'css' ? 'HTML (optional — a shell is used if empty)' : 'HTML'}
              <textarea
                value={html}
                onChange={(event) => setHtml(event.target.value)}
                rows={source === 'paste' ? 8 : 5}
                spellCheck={false}
                className="mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 font-mono text-[11px] text-ink outline-none focus:border-brand"
                placeholder="<section>…</section>"
              />
            </label>
          )}

          {(source === 'paste' || source === 'html' || source === 'css') && (
            <label className="block text-xs font-medium text-ink">
              {source === 'css' ? 'CSS (paste or upload below)' : 'Optional CSS'}
              <textarea
                value={css}
                onChange={(event) => setCss(event.target.value)}
                rows={source === 'css' ? 5 : 3}
                spellCheck={false}
                className="mt-1 w-full rounded-lg border border-line bg-shell px-2.5 py-2 font-mono text-[11px] text-ink outline-none focus:border-brand"
                placeholder=".hero { … }"
              />
            </label>
          )}

          {source !== 'paste' && (
            <label className="flex cursor-pointer flex-col items-center gap-2 rounded-xl border border-dashed border-line bg-shell/60 px-3 py-6 text-center">
              <Upload className="h-5 w-5 text-muted" />
              <span className="text-xs font-semibold text-ink">
                {file
                  ? file.name
                  : source === 'zip'
                    ? 'Choose a ZIP file'
                    : source === 'css'
                      ? 'Choose a .css file (optional if pasted above)'
                      : 'Choose an .html file'}
              </span>
              <input
                type="file"
                accept={
                  source === 'zip'
                    ? '.zip,application/zip'
                    : source === 'css'
                      ? '.css,text/css'
                      : '.html,.htm,text/html'
                }
                className="hidden"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
            </label>
          )}

          <p className="rounded-lg border border-line bg-shell/50 px-3 py-2 text-[11px] text-muted">
            <FileCode2 className="mr-1 inline h-3.5 w-3.5" />
            Full HTML documents are supported — the body is extracted. Tailwind utilities are not compiled; include CSS when you need those styles.
          </p>

          {error && <p className="text-xs font-semibold text-danger">{error}</p>}
          {warnings.length > 0 && (
            <ul className="list-disc space-y-1 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-[11px] text-amber-900">
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-lg border border-line bg-card px-3 py-2 text-xs font-semibold text-ink hover:bg-shell">
            Cancel
          </button>
          <button
            type="button"
            disabled={pending}
            onClick={() => void submit()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-xs font-semibold text-white hover:bg-brand/90 disabled:opacity-50"
          >
            {pending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            {pending ? 'Importing…' : 'Import section'}
          </button>
        </div>
      </div>
    </div>
  );
}
